import { useTranslation } from 'react-i18next'
import { isApiError } from '@dorixona/api'
import { Press } from '@dorixona/ui'
import { Icon } from './Icon'
import type { Translate } from './translate'

export type LoadingShape = 'products' | 'rows' | 'tiles' | 'page' | 'lines'

export interface LoadingProps {
  /** Текст для читалки экрана: глазом видна заготовка, а не слова. */
  what: string
  /**
   * Форма заготовки — та же, что у содержимого, которое вот-вот придёт:
   * сетка товаров, строки списка, плитки категорий, страница товара или
   * несколько строк текста внутри карточки.
   */
  shape?: LoadingShape
  count?: number
}

const repeat = (n: number) => Array.from({ length: n }, (_, i) => i)

/**
 * Данные ещё идут. Вместо слова «Загрузка» — заготовка в форме будущего
 * содержимого: экран не прыгает, когда данные приходят, и сразу видно,
 * что здесь будет. Мерцание гаснет при «уменьшить движение».
 */
export function Loading({ what, shape = 'rows', count }: LoadingProps) {
  return (
    <div className={`sk sk-${shape}`} role="status" aria-busy="true">
      <span className="visually-hidden">{what}</span>
      {shape === 'products' ? (
        <div className="grid" aria-hidden="true">
          {repeat(count ?? 4).map((i) => (
            <div key={i} className="sk-pcard">
              <i className="sk-b sk-img" />
              <i className="sk-b sk-l" />
              <i className="sk-b sk-l sk-s" />
              <i className="sk-b sk-l sk-price" />
              <i className="sk-b sk-btn" />
            </div>
          ))}
        </div>
      ) : null}
      {shape === 'rows' ? (
        <div className="card sk-list" aria-hidden="true">
          {repeat(count ?? 3).map((i) => (
            <div key={i} className="sk-row">
              <i className="sk-b sk-ic" />
              <span>
                <i className="sk-b sk-l" />
                <i className="sk-b sk-l sk-s" />
              </span>
            </div>
          ))}
        </div>
      ) : null}
      {shape === 'tiles' ? (
        <div className="cattiles" aria-hidden="true">
          {repeat(count ?? 4).map((i) => (
            <div key={i} className="cattile sk-tile">
              <i className="sk-b sk-ic" />
              <span>
                <i className="sk-b sk-l" />
                <i className="sk-b sk-l sk-s" />
              </span>
            </div>
          ))}
        </div>
      ) : null}
      {shape === 'page' ? (
        <div className="pp-card sk-page" aria-hidden="true">
          <i className="sk-b sk-hero" />
          <i className="sk-b sk-l sk-title" />
          <i className="sk-b sk-l sk-s" />
          <i className="sk-b sk-l sk-price" />
          <i className="sk-b sk-btn" />
        </div>
      ) : null}
      {shape === 'lines' ? (
        <div className="sk-lines" aria-hidden="true">
          {repeat(count ?? 3).map((i) => (
            <i key={i} className="sk-b sk-l" />
          ))}
        </div>
      ) : null}
    </div>
  )
}

/**
 * Что сказать покупателю про отказ. Ветвимся по коду ошибки, а не по её
 * сообщению: сообщение сервер пишет для журнала (docs/frontend/README.md).
 *
 * Покупателю не показываем ни кодов, ни номеров запроса — ему нужно понять,
 * что делать, а не чем это кончилось у нас.
 */
export function failureText(error: unknown, t: Translate): string {
  if (!isApiError(error)) return t('common:failure.network')
  if (error.code === 'customer_blocked') return t('common:failure.blocked')
  if (error.code === 'not_found') return t('common:failure.notFound')
  if (error.status >= 500) return t('common:failure.server')
  return t('common:failure.generic')
}

export interface FailedProps {
  error: unknown
  onRetry: () => void
}

/** Запрос не прошёл: что делать дальше и кнопка «ещё раз». */
export function Failed({ error, onRetry }: FailedProps) {
  const { t } = useTranslation('common')
  return (
    <div className="card empty" role="alert">
      <Icon name="i-search" size={54} />
      <div className="t">{failureText(error, t)}</div>
      <Press squish="chip" className="fbtn" onPress={onRetry}>
        {t('actions.retry')}
      </Press>
    </div>
  )
}
